import { SuperInput } from "@components:SuperInput/SuperInput";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdExpandMore } from "react-icons/md";

import { FieldNode } from "../types";
import { useNullableField } from "../useNullableField";
import { FieldLabel } from "./FieldLabel";
import { FieldBody } from "./FieldRenderer";

/*
 * A top-level property as a collapsible section, with a switch when the property is optional.
 */
export const Section = memo(
  ({ node, index }: { node: FieldNode; index: number }) => {
    const { t } = useTranslation();
    const { enabled, toggle } = useNullableField(node);
    const canToggle = node.nullable && !node.required;
    const [expanded, setExpanded] = useState(node.required || index === 0);

    return (
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        sx={{
          "&.MuiAccordion-root": {
            mt: 2,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 4,
            boxShadow: "none",
            "&::before": { content: "unset" },
          },
        }}
      >
        <AccordionSummary
          expandIcon={<MdExpandMore />}
          sx={{
            bgcolor: "background.default",
            border: "none",
            borderColor: "divider",
            borderRadius: 4,
            "& .MuiAccordionSummary-content": { mb: 0, mt: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            {canToggle && (
              <Box
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <SuperInput.Switch
                  checked={enabled}
                  onChange={(e) => {
                    toggle(e.target.checked);
                    setExpanded(e.target.checked);
                  }}
                  inputProps={{
                    "aria-label": t("schemaForm.enabledLabel", {
                      name: node.title,
                    }),
                  }}
                />
              </Box>
            )}

            <Box sx={{ flex: 1 }}>
              <FieldLabel
                title={node.title}
                description={node.description}
                required={node.required}
                sx={{ mb: 2 }}
              />
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 2, pb: 0 }}>
          {enabled || !canToggle ? (
            <FieldBody node={node} />
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic", mb: 2 }}
            >
              {t("schemaForm.enableToConfigure")}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    );
  },
);

Section.displayName = "Section";
